import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { Input, TextArea } from "sr/helpers";
import DropDown from "sr/helpers/ui-components/DropDown.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { createTasklist } from "sr/utils/api/company/customer/createTasklist";
import tasklistSchema from "sr/utils/schemas/company/customer/TasklistSchema";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { SuccessMessage } from "sr/helpers/functions/SuccessMessage";
import { toast } from "react-toastify";
import { getTask } from "sr/utils/api/company/customer/getTask";
import { updateChecklist } from "sr/utils/api/company/customer/updateChecklist";
interface TabRef {
  submitForm: () => void;
}
export interface ChecklistFormType {
  dataAddedHandle?: (arg: any) => void | null;
  nextPosition?: number;
  checklistId?: string;
  customerId?: string;
}
const TaskForm: ForwardRefRenderFunction<TabRef, ChecklistFormType> = (
  {
    dataAddedHandle = null,
    nextPosition = 1,
    checklistId: checklistIdProp = "",
    customerId: customerIdProp = "",
  },
  ref
) => {
  const [task, setTask] = useState("");
  const [description, setDescription] = useState("");
  const [selectType, setselectType] = useState("Optional");
  const [customerId, setCustomerId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [checklistId, setchecklistId] = useState(null);
  const [loader, setLoader] = useState(false);
  const intl = useIntl();
  const [position, setPosition] = useState(1);
  const [isMediaUploadRequired, setIsMediaUploadRequired] = useState(false);
  const activeChecklistId = checklistIdProp || checklistId;
  const activeCustomerId = customerIdProp || customerId;

  const params = useParams();

  useEffect(() => {
    const getTasks = async () => {
      try {
        const queryParams = new URLSearchParams();

        queryParams.append("status", "active");

        if (activeChecklistId) {
          queryParams.append("checklist_id", activeChecklistId.toString());
        }

        const res = await getTask(queryParams);

        if (res?.success) {
          const sorted = [...res.data].sort((a, b) => a.position - b.position);

          const lastPosition =
            sorted.length > 0 ? sorted[sorted.length - 1].position : 0;

          setPosition(lastPosition + 1);
        }
      } catch (e) {
        console.log(e);
      }
    };

    if (activeChecklistId) {
      getTasks();
    }
  }, [activeChecklistId]);

  useEffect(() => {
    const userDetails = getUserDetails();
    setCompanyId(userDetails?.user_id);
  }, []);
  useEffect(() => {
    const { id, checklistId, instruction } = params;
    setCustomerId(id);
    setchecklistId(checklistId || instruction);
  }, [params]);

  const InitialValues = {
    company_id: companyId,
    customer_id: activeCustomerId,
    checklist_id: activeChecklistId,
    name: task,
    type: selectType,
    description: description,
    status: "active",
    position: position,
    is_media_upload_required: isMediaUploadRequired,
  };

  const { errors, submitForm } = useFormik({
    initialValues: InitialValues,
    enableReinitialize: true,
    validationSchema: tasklistSchema,
    onSubmit: async (values) => {
      setLoader(true);
      if (!values?.checklist_id) {
        toast.error("Checklist Not Found");
        setLoader(false);
        return;
      }
      try {
        const res = await createTasklist(values);
        if (res.success === true) {
          await updateChecklist(
            {
              id: values.checklist_id,
              status: "active",
            },
            null,
          );
          SuccessMessage("API.SUCCESS.CUSTOMER.TASKLIST.ADD");
          dataAddedHandle("Task");
          setTask("");
          setDescription("");
          setselectType("");
          setIsMediaUploadRequired(false);
          setLoader(false);
        } else {
          setLoader(false);
        }
      } catch (e) {
        setLoader(false);
      }
    },
  });

  React.useImperativeHandle(ref, () => ({
    submitForm,
  }));
  return (
    <div>
      {loader && <LoaderFullScreen />}
      <Input
        onChange={(e) => setTask(e.target.value)}
        error={errors.name}
        value={task}
        InputDivClass="mt-2"
        label={
          <FormattedMessage
            id="RECRUITER.CUSTOMER.INPUT.CHECKLIST.TASK.TITLE"
            defaultMessage="Task"
          />
        }
        placeholder={""}
        type={"text"}
        inputClass="border border-zinc-800 rounded-lg h-11"
      ></Input>
      <div className="mt-10">
        <TextArea
          error={errors.description}
          labelClass={"text-normal  text-sm"}
          rows={4}
          label={
            <FormattedMessage
              id="RECRUITER.CUSTOMER.INPUT.CHECKLIST.DESCRIPTION.TITLE"
              defaultMessage="Description"
            />
          }
          className={
            "block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:border-[#0078ae] focus:shadow-inputshadow outline-0"
          }
          htmlFor="Description"
          placeholder={intl.formatMessage({
            id: "RECRUITER.CUSTOMER.INPUT.CHECKLIST.DESCRIPTION.PLACEHOLDER",
            defaultMessage: "Write your Description here...",
          })}
          onChange={(e) => setDescription(e.target.value)}
          value={description}
        ></TextArea>
      </div>

      <DropDown
        error={errors.type}
        onChange={(e) => setselectType(e.target.value)}
        value={selectType}
        dropdowndiv="text-gray-700 mt-10"
        labelMargin="mb-1"
        label={
          <FormattedMessage
            id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TYPE.TITLE"
            defaultMessage="Type"
          />
        }
        className="border border-zinc-800 rounded-lg h-11"
      >
        <option value="">
          <FormattedMessage
            id="RECRUITER.CUSTOMER.CHECKLIST.TASK.INPUT.SELECT.TYPE"
            defaultMessage="Select Type"
          />
        </option>
        <option value="Mandatory">
          <FormattedMessage
            id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TYPE.MANDATORY"
            defaultMessage="Mandatory"
          />
        </option>
        <option value="Optional">
          <FormattedMessage
            id="RECRUITER.CUSTOMER.CHECKLIST.TASK.TYPE.OPTIONAL"
            defaultMessage="Optional"
          />
        </option>
      </DropDown>

      <div className="mt-10">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isMediaUploadRequired}
            onChange={(e) => setIsMediaUploadRequired(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            <FormattedMessage
              id="RECRUITER.CUSTOMER.CHECKLIST.TASK.MANDATORY.MEDIA.LABEL"
              defaultMessage="Mandatory Media"
            />
          </span>
          <div className="relative group">
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs text-white bg-gray-400 rounded-full cursor-help hover:bg-gray-500">
              ?
            </span>
            <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-sm rounded-lg px-3 py-2 w-64 left-0 top-6 shadow-lg">
              Selecting Mandatory Media will require cleaners to attach a photo or a video to the associated task. Cleaners will not be able to complete the Checklist without attaching media.
              <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -top-1 left-6"></div>
            </div>
          </div>
        </label>
      </div>
    </div>
  );
};

export default forwardRef(TaskForm);
